/**
 * The PAC intro call as it is recorded on the chart — derivations shared by the
 * Orbit card and the Intake & billing panel.
 *
 * Same note as before the rebuild: `ci-intro-call-note`, the
 * `ci-intro-call-answers` payload, `visitType: 'inbound'`. BAR-2612 changed the
 * script and the card, not the resource, so every note written by the old
 * inbound card still parses and still renders.
 *
 * Parsing, draft state and "has anything been answered" are the intro-call
 * note's (`patient-access-note.ts`); only what this script records differently
 * lives here.
 */

import { answerString, answerStrings, PacIntroCallAnswers, PacVisitType } from './patient-access-note';
import { PAC_INTRO_CALL_BOOKED_OUTCOME, PacIntroCallOutcome } from './pac-intro-call-script';

/** The visit type a note must carry to render as the PAC intro call card. */
export const PAC_INTRO_CALL_VISIT_TYPE: PacVisitType = 'inbound';

/** The intro call's answers are the shared intro-call note's, on the inbound visit type. */
export type PacIntroCallNoteAnswers = PacIntroCallAnswers;

export const EMPTY_PAC_INTRO_CALL_ANSWERS: PacIntroCallNoteAnswers = {
  visitType: PAC_INTRO_CALL_VISIT_TYPE,
  values: {},
};

/** Whether a note on this category is the PAC intro call rather than one of the other two scripts. */
export function isPacIntroCallAnswers(answers: Pick<PacIntroCallAnswers, 'visitType'>): boolean {
  return answers.visitType === PAC_INTRO_CALL_VISIT_TYPE;
}

export function getPacIntroCallOutcome(answers: PacIntroCallNoteAnswers): PacIntroCallOutcome | undefined {
  return answerString(answers.values.close) as PacIntroCallOutcome | undefined;
}

/**
 * Every outcome the old vocabulary graded as a positive close. Retiring a
 * vocabulary must not silently re-grade the notes written under it, and both of
 * these were positive on `POSITIVE_OUTCOMES` before BAR-2612 — restoring only
 * `Booked` would flip every "to send a booking link" note to the warning glyph.
 */
const LEGACY_POSITIVE_OUTCOMES = ['Booked', 'To send a booking link'];

/** Only a booked consultation renders the check glyph; anything else renders the warning glyph. */
export function isBookedOutcome(outcome: string | undefined): boolean {
  return outcome === PAC_INTRO_CALL_BOOKED_OUTCOME || (!!outcome && LEGACY_POSITIVE_OUTCOMES.includes(outcome));
}

/** The collapsed preview line: outcome · barriers. No likelihood — the outcome is the read. */
export function buildPacIntroCallPreview(answers: PacIntroCallNoteAnswers): {
  outcome?: string;
  detail: string;
  positive: boolean;
} {
  const outcome = getPacIntroCallOutcome(answers);
  return {
    outcome,
    detail: answerStrings(answers.values.barrier).join(' · '),
    positive: isBookedOutcome(outcome),
  };
}

/**
 * Human-readable summary stored on the `text-content` payload, so timeline
 * search and any generic Communication renderer show something meaningful.
 * Deliberately terse — the card itself is the full record.
 */
export function buildPacIntroCallSummary(answers: PacIntroCallNoteAnswers): string {
  const lines: string[] = ['PAC intro call'];

  if (answers.slot) {
    const withName = answers.slot.practitionerName ? ` with ${answers.slot.practitionerName}` : '';
    lines.push(`Consultation booked: ${answers.slot.start}${withName}`);
  }

  const outcome = getPacIntroCallOutcome(answers);
  if (outcome) lines.push(`Outcome: ${outcome}`);

  const motivations = answerStrings(answers.values.motivation);
  if (motivations.length > 0) lines.push(`Motivation: ${motivations.join(', ')}`);

  const barriers = answerStrings(answers.values.barrier);
  if (barriers.length > 0) lines.push(`Barriers: ${barriers.join(', ')}`);

  const funFacts = answerString(answers.values.fun_facts);
  if (funFacts) lines.push(`Notes: ${funFacts}`);

  return lines.join('\n');
}
